// ─────────────────────────────────────────────────────────────────────────────
// "THE ORDER HAD NO NOTE. SO WHAT HAPPENED?"
//
// The tick answered one question — was this chart looked at — and left the one a reviewer
// actually asks a week later unanswered. Three answers are possible and they are not the
// same fact:
//
//   NOTE_ADDED     somebody wrote the note. The chart is now complete.
//   NOT_REQUIRED   a judgement: a routine reorder, a continuation, something the facility's
//                  own documentation policy does not oblige a note for.
//   REFERRED       passed to the DON or the unit manager. Not resolved — assigned.
//
// ── WHAT CHOOSING ONE DOES, AND WHAT IT DOES NOT ────────────────────────────
//
// It does NOT change a mark. The columns above are computed from the orders and the notes in
// the window, and a person's claim about what they did is recorded BESIDE them, never inside.
// The Medications/Orders column stays exactly as it is until a note actually appears in the
// window — because it reads the chart and not the click. A disposition that turned a column
// green would be a second, shallower audit trail competing with the signed worksheet's.
//
// What it DOES is stop the nightly alert reporting this chart again. That is the one thing
// that makes it worth collecting: an alert that keeps repeating a chart somebody has already
// judged is an alert people stop reading. The suppression is attributable — who, when, which
// of the three — and the alert prints the count of suppressed charts next to the open ones,
// so a facility answering NOT_REQUIRED to everything is visible rather than quiet.
//
// ── WHY ONE OF THE THREE OWES A REASON ──────────────────────────────────────
//
// NOTE_ADDED and REFERRED are checkable against the record: a note either arrived or it did
// not, and a referral names a person. NOT_REQUIRED is the one nothing else can verify — it
// asserts the facility's documentation policy did not oblige a note here, and an unexplained
// assertion of that is exactly the shape a surveyor asks about and nobody can answer. So that
// one carries its reason or it is not accepted.
//
// The rule is enforced in three places on purpose: here (so the button cannot be pressed into
// a rejection), in chart-check-store.mjs (so the API answers 400 with a sentence rather than a
// constraint name), and as a CHECK constraint in migration 074 (so it is true of the table).
// This copy is a courtesy to the person typing; it is not the enforcement.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";

/** The three answers, in the order they read: resolved, judged, assigned. */
export const DISPOSITIONS = [
  { key: "NOTE_ADDED", label: "Note added", hint: "The note has been written in PointClickCare." },
  { key: "NOT_REQUIRED", label: "No note needed", hint: "Your documentation policy does not oblige one here." },
  { key: "REFERRED", label: "Referred", hint: "Passed to the DON or the unit manager." },
];

export const NEEDS_REASON = "NOT_REQUIRED";
export const MIN_REASON = 10;

/** Does this answer need a reason typed before it can be saved? */
export function needsReason(disposition) {
  return disposition === NEEDS_REASON;
}

/** Is what has been typed enough? Trimmed, because whitespace is not a reason. */
export function reasonOk(disposition, note) {
  if (!needsReason(disposition)) return true;
  return String(note || "").trim().length >= MIN_REASON;
}

const CHIP = (selected) => ({
  padding: "4px 9px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
  background: selected ? "var(--ed-brand-soft)" : "var(--ed-canvas)",
  color: selected ? "var(--ed-ink)" : "var(--ed-ink-2)",
  border: `1px solid ${selected ? "var(--ed-brand)" : "var(--ed-line)"}`,
});

/**
 * @param {string|null} value        the answer already recorded, if any
 * @param {string|null} note         its reason, if any
 * @param {boolean} busy             a save is in flight
 * @param {string|null} unsaved      why the LAST save did not store the answer — shown as
 *                                   its own sentence, because "we saved the tick but not the
 *                                   answer" is a real third state (migration 074 unapplied)
 * @param {(disposition: string, note: string|null) => void} onChoose
 */
export default function DispositionPicker({ value = null, note = null, busy = false, unsaved = null, onChoose }) {
  // Which chip is being filled in, as opposed to which answer is recorded. Opening the reason
  // box is not an answer, and closing it without typing one leaves the record untouched.
  const [drafting, setDrafting] = useState(null);
  const [draft, setDraft] = useState("");

  const pick = (key) => {
    if (needsReason(key)) {
      // The one that owes a reason opens its box rather than saving. Pre-filled with the
      // reason already on file, so correcting a typo is not retyping the sentence.
      setDrafting(key);
      setDraft(value === key && note ? String(note) : "");
      return;
    }
    setDrafting(null);
    onChoose(key, null);
  };

  const current = DISPOSITIONS.find((d) => d.key === value) || null;
  const short = (n) => Math.max(0, MIN_REASON - String(n || "").trim().length);

  return (
    <div style={{ margin: "0 0 10px 0", padding: "8px 10px", borderRadius: 8, background: "var(--ed-line-2)" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--ed-ink-2)" }}>
          The order had no note — what happened?
        </span>
        {DISPOSITIONS.map((d) => (
          <button
            key={d.key}
            type="button"
            disabled={busy}
            onClick={() => pick(d.key)}
            title={d.hint}
            aria-pressed={value === d.key}
            style={{ ...CHIP(value === d.key), opacity: busy ? 0.55 : 1 }}
          >
            {d.label}
          </button>
        ))}
        {/* OPTIONAL, AND SAID SO. A chart with no answer is a real state — it is what every
            tick made before this existed means — and a person who does not want to answer
            must not be left looking for the way out. */}
        <span style={{ fontSize: 10, color: "var(--ed-muted)" }}>optional</span>
      </div>

      {drafting ? (
        <div style={{ marginTop: 7 }}>
          <label
            htmlFor="dispo-reason"
            style={{ display: "block", fontSize: 10, color: "var(--ed-ink-2)", marginBottom: 3 }}
          >
            Why no note was needed — a surveyor reading this record will ask, and this is the
            only place the answer exists.
          </label>
          <textarea
            id="dispo-reason"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            style={{
              width: "100%",
              boxSizing: "border-box",
              fontFamily: "inherit",
              fontSize: 11,
              padding: "5px 7px",
              borderRadius: 6,
              border: "1px solid var(--ed-line)",
              background: "var(--ed-canvas)",
              color: "var(--ed-ink)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5 }}>
            <button
              type="button"
              disabled={busy || !reasonOk(drafting, draft)}
              onClick={() => {
                onChoose(drafting, draft.trim());
                setDrafting(null);
              }}
              style={{
                ...CHIP(true),
                cursor: reasonOk(drafting, draft) && !busy ? "pointer" : "default",
                opacity: reasonOk(drafting, draft) && !busy ? 1 : 0.5,
              }}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setDrafting(null)}
              style={{ ...CHIP(false), cursor: "pointer" }}
            >
              Cancel
            </button>
            {/* THE REASON IT IS DISABLED, NOT JUST THAT IT IS. A greyed button with no
                explanation is read as a broken screen. */}
            {reasonOk(drafting, draft) ? null : (
              <span style={{ fontSize: 10, color: "var(--ed-muted)" }}>
                {short(draft)} more character{short(draft) === 1 ? "" : "s"} — this one has to say why
              </span>
            )}
          </div>
        </div>
      ) : null}

      {current ? (
        <div style={{ marginTop: 6, fontSize: 10, color: "var(--ed-ink-2)", lineHeight: 1.5 }}>
          Recorded as <strong>{current.label}</strong>
          {note ? ` — "${note}"` : ""}. The nightly alert will stop reporting this chart.{" "}
          {/* SAID EVERY TIME, not once in a legend. The expectation it corrects — that
              answering turns the column green — is the one a person forms by acting. */}
          <span style={{ color: "var(--ed-muted)" }}>
            The marks above do not change: they read the chart, not this answer.
          </span>
        </div>
      ) : null}

      {unsaved ? (
        <div style={{ marginTop: 6, fontSize: 10, color: "var(--ed-amber-deep)", lineHeight: 1.5 }}>
          <strong>The tick saved; the answer did not.</strong> {unsaved}
        </div>
      ) : null}
    </div>
  );
}
